import React from 'react';
import {
  TextField,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
} from '@material-ui/core';
import {
  AccountBalanceWallet as CoinsIcon,
  TrendingUp as TrendingUpIcon,
  Receipt as ReceiptIcon,
} from '@material-ui/icons';

const Alert = ({ children, severity = 'info', icon }) => (
  <Paper style={{ marginTop: 8, padding: 8, backgroundColor: severity === 'success' ? '#e8f5e9' : '#e3f2fd' }}>
    <Typography variant="caption">
      {icon} {children}
    </Typography>
  </Paper>
);

const formatCurrencyInput = value => {
  if (!value && value !== 0) {
    return '';
  }
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(numValue)) {
    return '';
  }
  return numValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const Pagamento = ({ formData, setFormData, classes }) => {
  return (
    <div className={classes.sectionGap}>
      {/* Pagamento Inicial (Assinatura do Contrato) */}
      <div>
        <Typography variant="h6" gutterBottom>
          Pagamento Inicial
        </Typography>
        <Grid container spacing={2} style={{ marginBottom: 12 }}>
          <Grid item xs={12} md={6}>
            <Card
              variant="outlined"
              className={`${classes.cardSelectable} ${formData.payment.hasInitialPayment ? classes.cardSelected : ''}`}
              onClick={() =>
                setFormData({
                  ...formData,
                  payment: { ...formData.payment, hasInitialPayment: true },
                })
              }
              style={{
                borderColor: formData.payment.hasInitialPayment ? '#4caf50' : undefined,
                backgroundColor: formData.payment.hasInitialPayment ? '#e8f5e920' : undefined,
              }}
            >
              <CardContent style={{ padding: 12, textAlign: 'center' }}>
                <div
                  className={classes.cardIcon}
                  style={{
                    backgroundColor: formData.payment.hasInitialPayment ? '#4caf50' : '#bdbdbd',
                  }}
                >
                  <CoinsIcon style={{ color: 'white', fontSize: 24 }} />
                </div>
                <Typography variant="subtitle1" gutterBottom>
                  Com Pagamento Inicial
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Pagamento único na assinatura
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              variant="outlined"
              className={classes.cardSelectable}
              onClick={() =>
                setFormData({
                  ...formData,
                  payment: { ...formData.payment, hasInitialPayment: false },
                })
              }
              style={{
                borderColor: !formData.payment.hasInitialPayment ? '#757575' : undefined,
                backgroundColor: !formData.payment.hasInitialPayment ? '#f5f5f5' : undefined,
              }}
            >
              <CardContent style={{ padding: 12, textAlign: 'center' }}>
                <div
                  className={classes.cardIcon}
                  style={{
                    backgroundColor: !formData.payment.hasInitialPayment ? '#757575' : '#bdbdbd',
                  }}
                >
                  <CoinsIcon style={{ color: 'white', fontSize: 24 }} />
                </div>
                <Typography variant="subtitle1" gutterBottom>
                  Sem Pagamento Inicial
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Sem comissão na assinatura
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {formData.payment.hasInitialPayment && (
          <Paper variant="outlined" className={classes.configSection}>
            <Typography variant="subtitle1" gutterBottom>
              Configuração do Pagamento Inicial
            </Typography>
            <Grid container spacing={2} style={{ marginBottom: 12 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={formData.payment.initialPaymentType}
                    label="Tipo"
                    onChange={e =>
                      setFormData({
                        ...formData,
                        payment: { ...formData.payment, initialPaymentType: e.target.value },
                      })
                    }
                  >
                    <MenuItem value="percent">Percentual (%)</MenuItem>
                    <MenuItem value="fixed">Valor Fixo (R$)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                {formData.payment.initialPaymentType === 'fixed' ? (
                  <TextField
                    label="Valor (R$)"
                    value={formatCurrencyInput(formData.payment.initialPaymentValue)}
                    onChange={e => {
                      const rawValue = e.target.value.replace(/\D/g, '');
                      const numericValue = rawValue ? parseInt(rawValue, 10) / 100 : '';
                      setFormData({
                        ...formData,
                        payment: { ...formData.payment, initialPaymentValue: numericValue },
                      });
                    }}
                    placeholder="Ex: 1.000,00"
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      startAdornment: <span style={{ marginRight: 4, color: '#666' }}>R$</span>,
                    }}
                  />
                ) : (
                  <TextField
                    label="Percentual (%)"
                    type="number"
                    value={formData.payment.initialPaymentValue}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        payment: { ...formData.payment, initialPaymentValue: e.target.value },
                      })
                    }
                    placeholder="Ex: 10"
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      endAdornment: <span style={{ marginLeft: 4, color: '#666' }}>%</span>,
                      inputProps: { step: '0.01', min: 0, max: 100 },
                    }}
                  />
                )}
              </Grid>
            </Grid>
            <Alert severity="success" icon="💰">
              {formData.payment.initialPaymentType === 'fixed'
                ? 'Valor fixo pago uma única vez na assinatura do contrato'
                : 'Percentual calculado sobre o valor total do contrato'}
            </Alert>
          </Paper>
        )}
      </div>

      {/* Pagamento de Fatura */}
      <div>
        <Typography variant="h6" gutterBottom>
          Pagamento de Fatura
        </Typography>
        <Grid container spacing={2} style={{ marginBottom: 12 }}>
          <Grid item xs={12} md={6}>
            <Card
              variant="outlined"
              className={`${classes.cardSelectable} ${formData.payment.hasInvoicePayment ? classes.cardSelected : ''}`}
              onClick={() =>
                setFormData({
                  ...formData,
                  payment: { ...formData.payment, hasInvoicePayment: true },
                })
              }
              style={{
                borderColor: formData.payment.hasInvoicePayment ? '#2196f3' : undefined,
                backgroundColor: formData.payment.hasInvoicePayment ? '#e3f2fd20' : undefined,
              }}
            >
              <CardContent style={{ padding: 12, textAlign: 'center' }}>
                <div
                  className={classes.cardIcon}
                  style={{
                    backgroundColor: formData.payment.hasInvoicePayment ? '#2196f3' : '#bdbdbd',
                  }}
                >
                  <ReceiptIcon style={{ color: 'white', fontSize: 24 }} />
                </div>
                <Typography variant="subtitle1" gutterBottom>
                  Com Pagamento de Fatura
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Comissão por fatura paga
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              variant="outlined"
              className={classes.cardSelectable}
              onClick={() =>
                setFormData({
                  ...formData,
                  payment: { ...formData.payment, hasInvoicePayment: false },
                })
              }
              style={{
                borderColor: !formData.payment.hasInvoicePayment ? '#757575' : undefined,
                backgroundColor: !formData.payment.hasInvoicePayment ? '#f5f5f5' : undefined,
              }}
            >
              <CardContent style={{ padding: 12, textAlign: 'center' }}>
                <div
                  className={classes.cardIcon}
                  style={{
                    backgroundColor: !formData.payment.hasInvoicePayment ? '#757575' : '#bdbdbd',
                  }}
                >
                  <ReceiptIcon style={{ color: 'white', fontSize: 24 }} />
                </div>
                <Typography variant="subtitle1" gutterBottom>
                  Sem Pagamento de Fatura
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Sem comissão por fatura
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {formData.payment.hasInvoicePayment && (
          <Paper variant="outlined" className={classes.configSection}>
            <Typography variant="subtitle1" gutterBottom>
              Configuração do Pagamento de Fatura
            </Typography>
            <Grid container spacing={2} style={{ marginBottom: 12 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={formData.payment.invoicePaymentType}
                    label="Tipo"
                    onChange={e =>
                      setFormData({
                        ...formData,
                        payment: { ...formData.payment, invoicePaymentType: e.target.value },
                      })
                    }
                  >
                    <MenuItem value="percent">Percentual (%)</MenuItem>
                    <MenuItem value="fixed">Valor Fixo (R$)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                {formData.payment.invoicePaymentType === 'fixed' ? (
                  <TextField
                    label="Valor (R$)"
                    value={formatCurrencyInput(formData.payment.invoicePaymentValue)}
                    onChange={e => {
                      const rawValue = e.target.value.replace(/\D/g, '');
                      const numericValue = rawValue ? parseInt(rawValue, 10) / 100 : '';
                      setFormData({
                        ...formData,
                        payment: { ...formData.payment, invoicePaymentValue: numericValue },
                      });
                    }}
                    placeholder="Ex: 50,00"
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      startAdornment: <span style={{ marginRight: 4, color: '#666' }}>R$</span>,
                    }}
                  />
                ) : (
                  <TextField
                    label="Percentual (%)"
                    type="number"
                    value={formData.payment.invoicePaymentValue}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        payment: { ...formData.payment, invoicePaymentValue: e.target.value },
                      })
                    }
                    placeholder="Ex: 5"
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      endAdornment: <span style={{ marginLeft: 4, color: '#666' }}>%</span>,
                      inputProps: { step: '0.01', min: 0, max: 100 },
                    }}
                  />
                )}
              </Grid>
            </Grid>
            <Alert severity="success" icon="🧾">
              {formData.payment.invoicePaymentType === 'fixed'
                ? 'Valor fixo pago por cada fatura do cliente'
                : 'Percentual calculado sobre o valor de cada fatura'}
            </Alert>
          </Paper>
        )}
      </div>

      {/* Pagamento Recorrente */}
      <div>
        <Typography variant="h6" gutterBottom>
          Pagamento Recorrente
        </Typography>
        <Grid container spacing={2} style={{ marginBottom: 12 }}>
          <Grid item xs={12} md={6}>
            <Card
              variant="outlined"
              className={`${classes.cardSelectable}`}
              onClick={() =>
                setFormData({
                  ...formData,
                  payment: { ...formData.payment, hasRecurrence: true },
                })
              }
              style={{
                borderColor: formData.payment.hasRecurrence ? '#9c27b0' : undefined,
                backgroundColor: formData.payment.hasRecurrence ? '#f3e5f520' : undefined,
              }}
            >
              <CardContent style={{ padding: 12, textAlign: 'center' }}>
                <div
                  className={classes.cardIcon}
                  style={{
                    backgroundColor: formData.payment.hasRecurrence ? '#9c27b0' : '#bdbdbd',
                  }}
                >
                  <TrendingUpIcon style={{ color: 'white', fontSize: 30 }} />
                </div>
                <Typography variant="subtitle1" gutterBottom>
                  Com Recorrência
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Pagamento mensal contínuo
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              variant="outlined"
              className={classes.cardSelectable}
              onClick={() =>
                setFormData({
                  ...formData,
                  payment: { ...formData.payment, hasRecurrence: false },
                })
              }
              style={{
                borderColor: !formData.payment.hasRecurrence ? '#757575' : undefined,
                backgroundColor: !formData.payment.hasRecurrence ? '#f5f5f5' : undefined,
              }}
            >
              <CardContent style={{ padding: 12, textAlign: 'center' }}>
                <div
                  className={classes.cardIcon}
                  style={{
                    backgroundColor: !formData.payment.hasRecurrence ? '#757575' : '#bdbdbd',
                  }}
                >
                  <TrendingUpIcon style={{ color: 'white', fontSize: 30 }} />
                </div>
                <Typography variant="subtitle1" gutterBottom>
                  Sem Recorrência
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Apenas pagamento único
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {formData.payment.hasRecurrence && (
          <Paper variant="outlined" className={classes.configSection}>
            <Typography variant="subtitle1" gutterBottom>
              Configuração do Pagamento Recorrente
            </Typography>
            <Grid container spacing={2} style={{ marginBottom: 12 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={formData.payment.recurrenceType}
                    label="Tipo"
                    onChange={e =>
                      setFormData({
                        ...formData,
                        payment: { ...formData.payment, recurrenceType: e.target.value },
                      })
                    }
                  >
                    <MenuItem value="percent">Percentual (%)</MenuItem>
                    <MenuItem value="fixed">Valor Fixo (R$)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                {formData.payment.recurrenceType === 'fixed' ? (
                  <TextField
                    label="Valor (R$)"
                    value={formatCurrencyInput(formData.payment.recurrenceValue)}
                    onChange={e => {
                      const rawValue = e.target.value.replace(/\D/g, '');
                      const numericValue = rawValue ? parseInt(rawValue, 10) / 100 : '';
                      setFormData({
                        ...formData,
                        payment: { ...formData.payment, recurrenceValue: numericValue },
                      });
                    }}
                    placeholder="Ex: 500,00"
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      startAdornment: <span style={{ marginRight: 4, color: '#666' }}>R$</span>,
                    }}
                  />
                ) : (
                  <TextField
                    label="Percentual (%)"
                    type="number"
                    value={formData.payment.recurrenceValue}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        payment: { ...formData.payment, recurrenceValue: e.target.value },
                      })
                    }
                    placeholder="Ex: 5"
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      endAdornment: <span style={{ marginLeft: 4, color: '#666' }}>%</span>,
                      inputProps: { step: '0.01', min: 0, max: 100 },
                    }}
                  />
                )}
              </Grid>
            </Grid>
            <Alert severity="success" icon="🔄">
              {formData.payment.recurrenceType === 'fixed'
                ? 'Valor fixo pago mensalmente durante a vigência do contrato'
                : 'Percentual calculado mensalmente sobre o faturamento'}
            </Alert>
          </Paper>
        )}
      </div>
    </div>
  );
};

export default Pagamento;
